<script setup lang="ts">
import { Hero } from '@clovyr/bed';
import heroGraphic from '@clovyr/bed/assets/images/clovyr-island-mascots.webp';
import gardenThicketBg from '@clovyr/bed/assets/images/clovyr-island-mascots-fg.svg';
import HeroBackground from '@clovyr/bed/components/elements/HeroBackground.vue';
import type { Bundle } from '@clovyr/pollen/types';
import { slugify } from '@clovyr/pollen/util/slugify';

import Tag from '@/components/elements/Tag.vue';
import AppBrowser from '@/components/tiers/AppBrowser.vue';
import { useRouteHead } from '@/composables/useRouteHead';
import { type ShowLoginEvent, useEventBus } from '@/stores/event_bus';
import { usePollenStore } from '@/stores/pollen_store';

const { login } = defineProps<{
  login?: ShowLoginEvent;
}>();

useRouteHead();

const { eventBus } = useEventBus();
const { garden } = usePollenStore();
const bundles = garden.getBundles().filter((b) => b.id !== 'fedimint');
const tags = garden.getTags();
const getCollectionApps = (bundle: Bundle) =>
  garden.getAppsByBundle(bundle.id).map((manifest) => manifest.metadata);

function showLoginForm(l: ShowLoginEvent | undefined) {
  if (!l) {
    eventBus.emit('modal:login:force_close');
    return;
  }
  void nextTick(() => {
    // show the modal only after loading, just a brief delay
    // if we don't the teleport component somehow breaks and cannot be cleanly removed from the page
    // but the problem only presents itself in the production build.
    eventBus.emit('modal:login:show', l);
  });
}

onMounted(async () => {
  showLoginForm(login);
});

watch(
  () => login,
  (newLogin) => {
    showLoginForm(newLogin);
  },
);
</script>

<template>
  <div class="garden">
    <Hero :subheadingTextShade="'light'">
      <template #heading>Welcome to<br />the Garden</template>
      <template #subheading>
        <div class="sub-heading">
          <span>A curated home for open-source apps you can launch with a single click.</span>
          <span class="free-trial"
            >Pick an app, launch it, and enjoy a free 7-day trial on us.</span
          >
        </div>
      </template>
      <template #background>
        <HeroBackground
          :background-image="heroGraphic"
          :overlayImage="gardenThicketBg"
          :gradient="15"
        />
      </template>
    </Hero>

    <div class="garden-middle container">
      <template v-for="bundle in bundles" :key="bundle.id">
        <AppBrowser
          v-if="getCollectionApps(bundle).length > 0"
          :bundle="bundle"
          :app-data="getCollectionApps(bundle)"
        />
      </template>
    </div>

    <!-- <div class="garden-bottom container">
      <div class="garden-tags row">
        <h2 class="garden-tags__left grid__col-md-3">Explore By Tag</h2>
        <div class="garden-tags__right grid__col-md-9">
          <template v-for="tag in tags" :key="slugify(tag)">
            <Tag v-if="tag" :label="tag" :slug="slugify(tag)" />
          </template>
        </div>
      </div>
    </div> -->
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/typography/fonts';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.garden {
  position: relative;
  padding-bottom: grid-vars.space(11);
  overflow: hidden;

  :deep(.hero) {
    .hero__inner {
      padding-top: clamp(#{grid-vars.space(15)}, 42vw, #{grid-vars.space(30)});
      padding-bottom: grid-vars.space(6);

      @include breakpoints.media-breakpoint-up(md) {
        padding-top: 0;
        padding-bottom: 0;
        padding-left: var(--col-gutter-outer-left);
      }

      .hero__middle {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 40rem;
        width: 100%;
        text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.6);

        @include breakpoints.media-breakpoint-up(md) {
          align-items: flex-start;
          text-shadow: none;
        }

        .hero__heading {
          text-align: center;
          margin-bottom: 0.5rem;

          @include fonts.font-size(32);

          @include breakpoints.media-breakpoint-up(md) {
            text-align: left;
          }

          @include breakpoints.media-breakpoint-up(md) {
            @include fonts.font-size(42);
          }

          @include breakpoints.media-breakpoint-up(xl) {
            @include fonts.font-size(67);
          }
        }

        .hero__subheading {
          @include fonts.font-size(16);
          margin-left: 0.25rem;

          text-align: center;

          @include breakpoints.media-breakpoint-up(md) {
            text-align: left;
          }
        }
      }
    }

    .hero-background {
      position: absolute;
      inset: 0;
      height: 100%;

      @include breakpoints.media-breakpoint-up(md) {
        position: relative;
        inset: auto;
        height: auto;
      }

      .background__overlay-image {
        background-position: center bottom;
        margin-top: 1px;
      }

      img {
        aspect-ratio: auto;
        height: 100%;
        object-position: top center;

        @include breakpoints.media-breakpoint-up(md) {
          aspect-ratio: 2/1;
          height: auto;
          object-position: center center;
        }

        @include breakpoints.media-breakpoint-up(lg) {
          aspect-ratio: 2.5/1;
          object-position: center center;
        }
      }
    }

    .hero__left {
      @include breakpoints.media-breakpoint-up(md) {
        padding-left: 1rem;
      }
    }
  }

  .sub-heading {
    max-width: 40rem;
    .free-trial {
      color: colors.color(collection-1, text-primary);
      margin-left: 5px;
    }
  }

  .garden-top {
    padding-top: grid-vars.space(35.5);
    margin-bottom: grid-vars.space(6);
  }

  .garden-middle {
    padding-top: grid-vars.space(6);
  }

  .garden-bottom {
    justify-content: center;
  }

  .garden-tags {
    justify-content: flex-start;
    padding-left: calc(var(--col-gutter-outer-left) + 0.9rem);

    &__left {
      padding-left: 0;
      line-height: 1.2;
      color: colors.color(grey, primary);
    }

    &__right {
      margin-top: 1rem;
      padding-left: 0;

      @include breakpoints.media-breakpoint-up(md) {
        margin-top: 0;
      }
    }
  }
}
</style>
